import { FC, useEffect, useState } from 'react';
import {
  Modal,
  ModalProps,
  Button as AntButton,
  Descriptions,
  Select,
} from 'antd';
import { getDetailCode } from '../../../../lib/util';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../store/rootReducer';
import {
  IGetSpeakingRubricItem,
  ISpeakingRubricItem,
  ISpeakingRubricItemScaleBody,
  ISpeakingRubricItemScaleModBody,
} from '../../../../models/newSpeaking.model';
import ItemSaveTable from '../ItemSaveTable';
import Button from '../../../Button';
import useNotification from '../../../../hooks/useNotification';
import useAxios from '../../../../hooks/useAxios';
import {
  postSpeakingRubricItem,
  putSpeakingRubricItem,
} from '../../../../api/newSpeaking.api';
import Loader from '../../../Loader';
import ErrorResponse from '../../../ErrorResponse';
import useAuth from '../../../../hooks/useAuth';
import ConfirmModal from '../../../modals/ConfirmModal';

interface IProps extends Pick<ModalProps, 'visible'> {
  onCancel(): void;
  disabled: boolean;
  data?: ISpeakingRubricItem;
  rubricNo: number;
  eduStepCode: string;
}

const ItemSaveModal: FC<IProps> = ({
  visible,
  onCancel,
  disabled,
  data,
  rubricNo,
  eduStepCode,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const TE0007 = getDetailCode(commonCodes, 'TE0007');
  const { successNotification, infoNotification } = useNotification();
  const {
    data: postRes,
    isLoading: postIsLoading,
    error: postError,
    execute: postExecute,
  } = useAxios<IGetSpeakingRubricItem>();
  const {
    data: putRes,
    isLoading: putIsLoading,
    error: putError,
    execute: putExecute,
  } = useAxios<IGetSpeakingRubricItem>();

  const [name, setName] = useState('');
  const [evaluationType, setEvaluationType] = useState('');
  const [scoreWeight, setScoreWeight] = useState<number | undefined>();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [scales, setScales] = useState<ISpeakingRubricItemScaleBody[]>([]);

  useEffect(() => {
    addScale(3);
  }, []);

  useEffect(() => {
    if (!postIsLoading && postRes) {
      successNotification(t('common.message.save1'));
      onCancel();
    }
  }, [postRes, postIsLoading]);

  useEffect(() => {
    if (!putIsLoading && putRes) {
      successNotification(t('common.message.save1'));
      onCancel();
    }
  }, [putRes, putIsLoading]);

  useEffect(() => {
    if (data) {
      setName(data.name);
      setEvaluationType(data.evaluationType);
      setScoreWeight(data.scoreWeight);
      setScales(data.scales);
    }
  }, [data]);

  const addScale = (length = 1) => {
    const newScales = [...scales];
    for (let i = 0; i < length; i++) {
      newScales.push({
        orderSeq: (newScales[newScales.length - 1]?.orderSeq ?? 0) + 1,
        descriptionKor: '',
        descriptionEng: '',
      });
    }
    setScales(newScales);
  };

  const onChangeScale = (
    value: string,
    index: number,
    key: keyof ISpeakingRubricItemScaleBody,
  ) => {
    const newScales = [...scales];
    switch (key) {
      case 'orderSeq':
      case 'score':
        if (isNaN(+value)) return;
        newScales[index][key] = +value;
        break;
      default:
        newScales[index][key] = value;
    }
    setScales(newScales);
  };

  const sortScale = (items: ISpeakingRubricItemScaleBody[]) => {
    items.sort((a, b) =>
      a.orderSeq < b.orderSeq ? -1 : a.orderSeq > b.orderSeq ? 1 : 0,
    );
    items.forEach((item, index) => (item.orderSeq = index + 1));
    return items;
  };

  const raiseScale = (currentOrderSeq: number) => {
    if (currentOrderSeq !== 1) {
      const newScales = [...scales];
      const findIndex = newScales.findIndex(
        ({ orderSeq }) => orderSeq === currentOrderSeq,
      );
      newScales[findIndex].orderSeq--;
      newScales[findIndex - 1].orderSeq++;
      setScales(sortScale(newScales));
    }
  };

  const lowerScale = (currentOrderSeq: number) => {
    if (currentOrderSeq !== scales.length) {
      const newScales = [...scales];
      const findIndex = newScales.findIndex(
        ({ orderSeq }) => orderSeq === currentOrderSeq,
      );
      newScales[findIndex].orderSeq++;
      newScales[findIndex + 1].orderSeq--;
      setScales(sortScale(newScales));
    }
  };

  const deleteScale = (currentOrderSeq: number) => {
    const newScales = [...scales];
    const findIndex = newScales.findIndex(
      ({ orderSeq }) => orderSeq === currentOrderSeq,
    );
    newScales.splice(findIndex, 1);
    setScales(sortScale(newScales));
  };

  const onSubmit = () => {
    if (writeAuthYn === 'N') {
      return infoNotification(t('cbt.speakRubric.message3'));
    }
    if (postIsLoading || putIsLoading || disabled) return;
    if (!name) return infoNotification(t('cbt.speakRubric.message1'));
    if (!evaluationType) return infoNotification(t('cbt.speakRubric.message4'));
    if (!scoreWeight || scoreWeight === 0)
      return infoNotification(t('cbt.speakRubric.message5'));
    if (scoreWeight > 100)
      return infoNotification(t('cbt.speakingRubric.4'));
    for (const scale of scales) {
      if (!scale.descriptionEng)
        return infoNotification(t('cbt.speakRubric.message6'));
      if (!scale.descriptionKor)
        return infoNotification(t('cbt.speakRubric.message7'));
      if (!scale.score || scale.score === 0)
        return infoNotification(t('cbt.speakRubric.message8'));
      if (scale.score > 100)
        return infoNotification(t('cbt.speakingRubric.5'));

      if (eduStepCode === 'MD') {
        if (!scale.scaleCode) {
          return infoNotification(t('cbt.speakingRubric.6'));
        }
      }
    }
    
    if (data) {
      const newScales: ISpeakingRubricItemScaleModBody[] = scales.map(
        (value) => {
          const newScale = { ...value, localCode: data.scales[0].localCode };
          if (newScale.tipDescriptionKor) {
            newScale.tipDescriptionEng = newScale.tipDescriptionKor;
          }
          return newScale;
        },
      );

      putExecute(
        putSpeakingRubricItem(data.evaluationNo, {
          evaluationType,
          name,
          scoreWeight,
          scales: newScales,
        }),
      );
    } else {
      scales.forEach(scale => {
        if (scale.tipDescriptionKor) {
          scale.tipDescriptionEng = scale.tipDescriptionKor;
        }
      });

      postExecute(
        postSpeakingRubricItem({
          rubricNo,
          name,
          evaluationType,
          scoreWeight,
          scales,
        }),
      );
    }
  };

  return (
    <>
      <Modal
        width={750}
        title={t('cbt.speakRubric.title2')}
        visible={visible}
        onCancel={onCancel}
        footer={[
          <div className='comm-btn__area justify-content-center'>	
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={() => setConfirmVisible(true)}
              disabled={disabled}
            >
              {t('common.button.save')}
            </AntButton>
            <AntButton key="cancel" className='btn-basic gray' onClick={onCancel}>
              {t('common.button.cancel')}
            </AntButton>
          </div>,
        ]}
      >
        {(postIsLoading || putIsLoading) && <Loader />}
        <ErrorResponse error={postError} />
        <ErrorResponse error={putError} />

        <div className="comm-table__area rowType mt-0">
          <Descriptions bordered column={2} className="w-100">
            <Descriptions.Item label={t('cbt.speakRubric.column1')} span={2}>
              <input
                disabled={disabled}
                type="text"
                className="form-control input-text"
                value={name}
                onChange={({ target: { value } }) => setName(value)}
              />
            </Descriptions.Item>
            <Descriptions.Item
              label={t('cbt.speakRubric.column6')}
              span={1}
              contentStyle={{ width: '30%' }}
            >
              <Select
                disabled={disabled}
                value={evaluationType}
                onChange={setEvaluationType}
                className="w-100"
              >
                {TE0007.map(({ korName, detailCode }, index) => (
                  <Select.Option key={`TE0007-${index}`} value={detailCode}>
                    {korName}
                  </Select.Option>
                ))}
              </Select>
            </Descriptions.Item>
            <Descriptions.Item label={t('cbt.speakRubric.column5')} span={1}>
              <input
                disabled={disabled}
                type="text"
                className="form-control input-text"
                value={scoreWeight}
                onChange={({ target: { value } }) => setScoreWeight(+value)}
              />
            </Descriptions.Item>
          </Descriptions>
        </div>
        <div>
          <div className="comm-bx__space">
            <div className="comm-btn__area right">
              <Button
                disabled={disabled}
                className="btn-basic pink-line"
                onClick={() => addScale()}
                text={t('common.button.add')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
          <ItemSaveTable
            data={scales}
            disabled={disabled}
            raiseScale={raiseScale}
            lowerScale={lowerScale}
            deleteScale={deleteScale}
            onChangeScale={onChangeScale}
            eduStepCode={eduStepCode}
          />
        </div>
      </Modal>

      {confirmVisible && (
        <ConfirmModal
          visible={confirmVisible}
          setVisible={setConfirmVisible}
          onConfirm={onSubmit}
          titleText={t('common.button.confirm')}
          messageText={t('cbt.speakingRubric.7')}
          confirmText={t('common.button.save')}
          cancelText={t('common.button.cancel')}
        />
      )}
    </>
  );
};
export default ItemSaveModal;
